import ApexCharts from 'apexcharts'
import { transitionBackground } from './utils/transitionBackground'
import { initViewportSizeListener, handleViewportChange, iconOffset } from './utils/viewportSizeListener'
import { getXTickPositions, getLengthToX } from './utils/chartHelpers'
import { chartOptions } from './utils/chartOptions'

// convert values to percentage. -5 = 0%, 0 = 50%, 5 = 100%
const convertValueToPercentage = (value) => {
  return (value + 5) * 10
}

const CommunityBlock = (function () {
  const communityElement = document.getElementById('js-community-block')

  if (!communityElement) {
    return { init: () => {} }
  }

  const rangeSlider = document.getElementById('rangeSlider')
  const spiral = document.getElementById('spiral-marker')
  const valueContainer = document.getElementById('js-value-container')
  const select = document.getElementById('js-community-group-select')

  // Full Dataset from CMS. Structure expected { "groupName1": { "year": 1, "year2": 2 }}
  const graphData = communityElement?.getAttribute('data-graph-data') ?? JSON.stringify({})
  const totalDataset = JSON.parse(graphData) ?? {}
  const categories = Object.keys(totalDataset) ?? []
  // Value used for the select
  let selectedCategory = categories[0]
  // Data to be displayed on the graph. Structure expected { "year": 1, "year2": 2 }
  let selectedDataset = totalDataset[selectedCategory] ?? {}
  let prevDisplayValue = 0
  let currentIndex = 0
  // Graph variables that need to be defined to be reused.
  let data,
    chart,
    path,
    totalLength,
    xTicks = []

  /** Move the position of the spiral marker. */
  const moveMarker = (xPosition, topOffset) => {
    spiral.style.setProperty('transition', 'all 0.5s ease-in-out')
    spiral.style.setProperty('left', `calc(${xPosition}% - ${iconOffset}px)`)
    spiral.style.setProperty('top', `calc(${topOffset}% - ${iconOffset}px)`)
  }

  /** Resets the marker to the left most x position (and background and slider value) based on dataset */
  const resetMarker = () => {
    rangeSlider.value = 0
    const orginalYearScore = Object.values(selectedDataset)[0]
    const displayValue = convertValueToPercentage(orginalYearScore)
    const topOffset = 100 - displayValue

    moveMarker(0, topOffset)

    valueContainer.style.backgroundPosition = 'center 100%'
    valueContainer.style.animation = 'all 1s ease-in-out'
    transitionBackground(prevDisplayValue, displayValue, valueContainer)
    prevDisplayValue = 0
  }

  select?.addEventListener('change', function () {
    selectedCategory = select.value
    selectedDataset = totalDataset[selectedCategory]
    rangeSlider.value = 0
    rangeSlider.setAttribute('max', Object.keys(selectedDataset).length - 1)

    prepareRangeSlider(selectedDataset)
    resetMarker()
    initChart(selectedDataset)
  })

  rangeSlider?.addEventListener('input', function () {
    const newIndex = parseInt(rangeSlider.value)

    // if newIndex is the disabled option [future year] don't move range slider
    if (newIndex == rangeSlider.max) {
      rangeSlider.value = currentIndex
      return
    }

    const orginalYearScore = Object.values(selectedDataset)[newIndex]
    const displayValue = convertValueToPercentage(orginalYearScore)

    const topOffset = 100 - displayValue
    const xPosition = (newIndex / rangeSlider.max) * 100
    moveMarker(xPosition, topOffset)

    updateStrokeOffset(path, xTicks[newIndex])

    // update current value option classname to active
    const options = document.querySelectorAll('#years option')
    options.forEach((option, index) => {
      if (index == newIndex) {
        option.classList.add('active')
      } else {
        option.classList.remove('active')
      }
    })

    transitionBackground(prevDisplayValue, displayValue, valueContainer)
    prevDisplayValue = displayValue

    currentIndex = newIndex
  })

  const prepareSelect = (dataset) => {
    const keys = Object.keys(dataset)

    // prepare an option for each key where the value is the key and the label is the capitalised key
    keys.forEach((key) => {
      let option = document.createElement('option')
      option.value = key
      option.label = key.charAt(0).toUpperCase() + key.slice(1)
      select.appendChild(option)
    })
  }

  const prepareRangeSlider = (dataset) => {
    // Hard code 2060 as the last year in the dataset
    const dataPoints = [...Object.keys(dataset), 2060]
    const datalist = document.getElementById('years')
    const step = 1
    rangeSlider.setAttribute('step', step)
    rangeSlider.setAttribute('max', dataPoints.length - 1)
    const yearOptions = dataPoints.map((element, index) => {
      let option = document.createElement('option')
      option.value = index
      option.label = element
      if (index == 0) {
        option.classList.add('active')
      }
      if (index == dataPoints.length - 1) {
        // disable the last option [2060]
        option.setAttribute('disabled', 'disabled')
      }
      return option
    })
    datalist.replaceChildren(...yearOptions)
  }

  // Updates the graph line to display the line until the given x position.
  function updateStrokeOffset(path, targetX, duration = 1000) {
    const lengthToX = getLengthToX(path, targetX)

    path.style.strokeDasharray = totalLength
    path.style.strokeDashoffset = totalLength
    path.getBoundingClientRect() // trigger reflow

    path.style.transition = `stroke-dashoffset ${duration}ms ease`
    path.style.strokeDashoffset = totalLength - lengthToX
  }

  const initChart = (selectedDataset) => {
    data = Object.values(selectedDataset).map((value) => convertValueToPercentage(value))

    // Add value to end of data array for hardcoded 2060 year
    const chartSeries = [{ name: 'Actual', data: [...data, 0] }]
    const chartContainer = document.querySelector('#chart')

    if (chartContainer.hasChildNodes()) {
      // Remove the existing chart instance
      chart.destroy()
    }

    chart = new ApexCharts(chartContainer, {
      ...chartOptions,
      series: chartSeries,
    })

    chart.render().then(() => {
      // update path
      path = document.querySelectorAll('.apexcharts-line-series path')[0]
      totalLength = path.getTotalLength()
      path.setAttribute('stroke-dasharray', totalLength)
      path.setAttribute('stroke-dashoffset', totalLength)

      xTicks = getXTickPositions()
    })
  }

  return {
    init: () => {
      initViewportSizeListener()
      handleViewportChange()
      prepareRangeSlider(selectedDataset)
      prepareSelect(totalDataset)
      initChart(selectedDataset)
      resetMarker()
    },
  }
})()

export default CommunityBlock
